import { useUIMode } from '../context/UIContext'

/**
 * Decide si un input de escaneo debe auto-enfocarse.
 *
 * ── El problema ──
 * Los inputs de escaneo tienen `autoFocus` porque el lector fisico necesita el
 * foco puesto. En un celular ese foco abre el teclado virtual y tapa media
 * pantalla, aunque el operario no vaya a tipear porque escanea con la camara.
 *
 * ── Por que no se resolvio con inputmode="none" ──
 * Es la solucion estandar y fue el primer intento, pero el navegador consulta
 * `inputmode` SOLO cuando el campo recibe el foco. Con `autoFocus` el campo ya
 * esta enfocado, asi que al tocarlo no hay un focus nuevo, Chrome nunca
 * reevalua el atributo y el teclado no aparece nunca mas. Los rodeos para
 * forzar esa reevaluacion (blur+focus, dentro y fuera del gesto) tampoco
 * funcionaron en Android.
 *
 * ── La solucion ──
 * No enfocar. El input arranca sin foco, asi que el toque del operario es un
 * foco normal y corriente y el teclado aparece por el camino nativo del
 * navegador. Sin atributos, sin listeners, sin carreras contra el gesto: no
 * queda nada que pueda interferir.
 *
 * ── Alcance ──
 * Aplica en TODO mobile, sin importar el modo de captura. En desktop no cambia
 * nada: ahi el foco automatico se mantiene.
 *
 * El lector fisico igual funciona sin foco: ScanTargetContext junta lo que
 * teclea y se lo entrega al CameraScanButton del campo activo, por el mismo
 * `onScan` que usa la camara. Asi el operario escanea sin tocar el campo y sin
 * teclado, en los dos modos de captura.
 */
export const useScanKeyboard = () => {
  const { isMobile, isCameraCaptureEnabled } = useUIMode()

  return {
    /**
     * En mobile no se auto-enfoca NUNCA, sin importar el modo de captura.
     *
     * Se decidio asi a proposito: el teclado virtual tapa media pantalla y el
     * foco automatico lo abre en cada montaje y en cada salto entre campos.
     *
     * El lector fisico no necesita el foco: lo cubre ScanTargetContext.
     */
    autoFocus: !isMobile,
    /**
     * Mismo flag que decide si se muestra el boton de camara. Se expone para que
     * los layouts puedan acomodarlo: varias pantallas ponen el input y su boton
     * de accion en fila, y ahi el boton de camara tiene que forzar la columna
     * para quedar entre los dos.
     *
     * Ojo: NO sirve para el foco. Ese depende solo de isMobile.
     */
    isCameraCapture: isCameraCaptureEnabled
  }
}
